import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Container,
  Typography,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import { setInterval, setSelectedInterval } from '../../../actions/graphsFilterActions';
import Page from '../../../components/Layout/Page';
import CustomHeader from '../../../components/General/CustomHeader';
import SoilMoistureChart from '../../../components/Highcharts/LineCharts/SoilMoistureChart/SoilMoistureChart';
import StackedSoilMoistureChart from '../../../components/Highcharts/LineCharts/StackedSoilMoistureChart/StackedSoilMoistureChart';
import WeekSelector from './WeekSelector';
import { ReduxState } from '../../../types';
import AlignedWeatherForecast from './components/AlignedWeatherForecast';
import IrrigationProgram from './irrigationProgram';
import { getDaysForThreeWeeks } from './utils';
import useStyles from '../../../components/Highcharts/styles';
import IrrigationChart from '../../../components/Highcharts/IrrigationChart/IrrigationChart';
import RootsChart from '../../../components/Highcharts/RootsChart/RootsChart';
import {
  CHART_TITLE_SECTOR_HUMEDAD_SUELO,
  CHART_TITLE_SECTOR_PROFUNDIDAD_RAICES,
  CHART_TITLE_SECTOR_SUMATORIA_HUMEDAD,
} from '../../../constants/graphs';
import type { RiegoSectorDetailProps } from './riego.types';

const RiegoSectorDetail = ({ tabPanel = false }: RiegoSectorDetailProps) => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const selectedSector = useSelector((state: ReduxState) => state.currentSector.selectedSector);
  const [selectedWeek, setSelectedWeek] = useState(() => {
    const today = dayjs();
    return today.startOf('week').add(1, 'day');
  });
  useEffect(() => {
    dispatch(setSelectedInterval(null));
  }, []);

  useEffect(() => {
    // Calculate the same 21-day range as used in the irrigation program
    const start = selectedWeek.subtract(7, 'day');
    const end = selectedWeek.add(13, 'day');

    dispatch(setInterval({
      startDate: start.startOf('day').format('YYYY-MM-DDTHH:mm:ss'),
      endDate: end.endOf('day').format('YYYY-MM-DDTHH:mm:ss'),
    }));
  }, [selectedWeek]);

  const handleWeekChange = (range: { start: dayjs.Dayjs, end: dayjs.Dayjs }) => {
    setSelectedWeek(range.start);
  };

  const days = useMemo(
    () => getDaysForThreeWeeks(selectedWeek),
    [selectedWeek],
  );

  return (
    <Page>
      <Container maxWidth={false}>
        <Box pb={4}>
          {tabPanel !== true && <CustomHeader title={`Riego / ${selectedSector?.name}`} />}
          <Box mt={2}>
            <WeekSelector onChange={handleWeekChange} />
            <div className={classes.dashboardContainer} style={{ marginTop: '16px' }}>
              <IrrigationChart
                chartView="dashboard"
                sectorId={selectedSector?.id || ''}
                farmId={selectedSector?.farm || ''}
                height={120}
              />
              <StackedSoilMoistureChart
                chartView="dashboard"
                sectorId={selectedSector?.id || ''}
                farmId={selectedSector?.farm || ''}
                height={150}
                title={CHART_TITLE_SECTOR_HUMEDAD_SUELO}
              />
              <RootsChart
                chartView='dashboard'
                sectorId={selectedSector?.id || ''}
                farmId={selectedSector?.farm || ''}
                height={150}
                hideXValues
                title={CHART_TITLE_SECTOR_PROFUNDIDAD_RAICES}
              />
              <SoilMoistureChart
                chartView="dashboard"
                sectorId={selectedSector?.id || ''}
                farmId={selectedSector?.farm || ''}
                height={200}
                title={CHART_TITLE_SECTOR_SUMATORIA_HUMEDAD}
              />
            </div>
            <Box mt={4}>
              <Typography variant="h4" gutterBottom>
                Clima
              </Typography>
              <AlignedWeatherForecast days={days} showHistoricalData={true} />
            </Box>
            {/* Programa de riego */}
            <Box mt={4}>
              <Typography variant="h4" gutterBottom>
                Programa de riego
              </Typography>
              <IrrigationProgram days={days} />
            </Box>
          </Box>
        </Box>
      </Container>
    </Page>
  );
};

export default RiegoSectorDetail;
